import {Component, OnInit} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule, NgForm} from '@angular/forms';
import {Router} from '@angular/router';
import {NgEventBus} from 'ng-event-bus';
import {HeaderComponent} from '../templates/header/header.component';
import {FooterComponent} from '../templates/footer/footer.component';
import {FirmSettingsService} from '../common/service/firm-settings.service';
import {AuthenticatorService} from '../common/service/authenticator.service';
import {SurveyFirm, FirmLogo} from './survey-firm.model';
import {Observable, catchError, of, switchMap} from 'rxjs';
import {AppConfigService} from '../common/service/app-config.service';
import {FirmLogoModalService} from '../common/component/modal';
import {ConfirmationModalService} from '../common/component/modal';
import {NlrFormFieldHasErrorPipe} from '../common/pipe/nlr-form-field-has-error.pipe';
import {NlrPatternDirective} from '../common/directive/nlr-pattern.directive';
import {PermissionsService} from '../common/service/permissions.service';

type Firms = {
    firmNumber: number;
    firmName: string;
    locality: string;
    postcode: string;
    phoneNumber: boolean;
    mobilePhoneNumber: string;
    facsimileNumber: string;
    email: string;
    invalid: boolean;
    dirty: boolean;
};

@Component({
    selector: 'nlr-plan-maintain-firm',
    imports: [HeaderComponent, FooterComponent, CommonModule, FormsModule, ReactiveFormsModule, NlrFormFieldHasErrorPipe, NlrPatternDirective],
    templateUrl: './maintain-firm.component.html',
    styleUrl: './maintain-firm.component.scss'
})
export class MaintainFirmComponent implements OnInit {
    surveyFirm: SurveyFirm;
    firms: Firms[];
    title = 'Maintain survey firm settings';
    formattedFirm = '';

    maxPdfWatermarkCharacters = 70;
    saveBtnDisabled = false;

    maintainPermissions = ['MAINTAIN_FIRM'];

    constructor(
        private http: HttpClient,
        private eventBus: NgEventBus,
        private firmSettingsService: FirmSettingsService,
        private AuthenticatorServiceService: AuthenticatorService,
        private router: Router,
        private appConfigService: AppConfigService,
        private firmLogoModalService: FirmLogoModalService,
        private confirmationModalService: ConfirmationModalService,
        private permissionsService: PermissionsService
    ) {}

    ngOnInit(): void {
        const subs = this.getFirmsSetting().subscribe({
            next: data => {
                const userFirmNumber = this.AuthenticatorServiceService.getUsersFirmNumber();
                this.firms = data ? data : ([] as Firms[]);

                if (!this.surveyFirm.firmNumber) {
                    const userFirms = this.firms.filter(function (row) {
                        return row.firmNumber === userFirmNumber;
                    });
                    if (userFirms.length) {
                        this.surveyFirm.firmNumber = userFirms[0].firmNumber;
                    }
                }
                this.formatFirm();
            },
            error: error => {
                console.error(this.title + '- Firms Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    getFirmsSetting(): Observable<Firms[]> {
        return this.firmSettingsService.load().pipe(
            switchMap(firstResult => {
                this.surveyFirm = firstResult ? {...firstResult} : ({} as SurveyFirm);

                this.surveyFirm.firmDefaultLodgementReceiptEmailAddress = this.surveyFirm.firmEmailAddress ? true : false;
                const url = this.appConfigService.settings.apiGatewayUrl + '/surveyingFirms';
                return this.http.get<Firms[]>(url, {transferCache: false});
            }),
            catchError(error => {
                console.error(this.title + '- Firms Load failed ' + error.message);
                return of([]);
            })
        );
    }

    formatFirm() {
        if (this.surveyFirm.firmNumber) {
            const firmNumber = this.surveyFirm.firmNumber;
            this.formattedFirm =
                this.firms
                    .filter(function (row) {
                        return row.firmNumber === firmNumber;
                    })
                    .map(function (firm) {
                        return firm.firmName;
                    })[0] || '';
        }
    }

    getImagePreviewString() {
        return this.surveyFirm.firmLogo && this.surveyFirm.firmLogo['fileName'] && this.surveyFirm.firmLogo['base64String']
        ? 'data:' + this.getMimeType(this.surveyFirm.firmLogo['fileName']) + ';base64, ' + this.surveyFirm.firmLogo['base64String']
        : '';
    }

    getMimeType(filename) {
        let mimeType = 'text/plain';
        switch (filename.substr(filename.lastIndexOf('.')).toUpperCase()) {
            case '.PNG':
                mimeType = 'image/png';
                break;
            case '.JPEG':
            case '.JPG':
                mimeType = 'image/jpeg';
                break;
            default:
                mimeType = 'text/plain';
        }
        return mimeType;
    }

    attachFirmLogo() {
        if (this.surveyFirm.firmNumber) {
            this.firmLogoModalService.showModal().subscribe(data => {
                this.surveyFirm.firmLogo['base64String'] = data['base64String'].result;
                this.surveyFirm.firmLogo['fileLocation'] = data['fileLocation'];
                this.surveyFirm.firmLogo['fileName'] = data['fileName'];
                this.saveBtnDisabled = false;
            });
        }

        console.info('MaintainFirmController', 'firm number does not exists');
    }

    cancel() {
        console.debug(this.title + ' : cancel');
        this.confirmationModalService
        .showModal({
            title: 'Warning',
            confirmationText: 'You are about to lose all changes. Are you sure you wish to continue?'
        })
        .subscribe(action => {
            if (action === 'yes') {
                this.router.navigate(['/redirect-home']);
            }
        });
    }

    clearFirmEmailAddress() {
        if (this.surveyFirm.firmEmailAddress) {
            delete this.surveyFirm.firmEmailAddress;
        }
    }

    getSanitizedModelJson() {
        return JSON.stringify(this.getSanitizedModel());
    }

    getModelJson() {
        return JSON.stringify(this.surveyFirm);
    }

    getPdfWatermarkCharactersRemaining() {
        console.debug(this.title, 'vm.getPdfWatermarkCharactersRemaining');

        if (!this.surveyFirm.defaultPdfWatermark) {
            return this.maxPdfWatermarkCharacters;
        }

        const newLineCount = this.surveyFirm.defaultPdfWatermark.split('\n').length - 1;
        const characterCount = this.surveyFirm.defaultPdfWatermark.length - newLineCount;
        return this.maxPdfWatermarkCharacters - characterCount;
    }

    getSanitizedModel(): SurveyFirm {
        const sanitizedModel = {...this.surveyFirm};
        if (sanitizedModel.firmDefaultLodgementReceiptEmailAddress !== undefined) {
            delete sanitizedModel.firmDefaultLodgementReceiptEmailAddress;
        }
        return sanitizedModel;
    }

    save(form: NgForm) {
        if (form.valid) {
            this.firmSettingsService.save(this.getSanitizedModel()).subscribe({
                next: data => {
                    this.surveyFirm = data;
                    this.router.navigate(['redirect-home']);
                },
                error: error => {
                    console.log(this.title + ' Error saving ' + error);
                }
            });
            this.saveBtnDisabled = true;
        }
    }

    clear() {
        this.surveyFirm.firmLogo = {} as FirmLogo;
        this.saveBtnDisabled = false;
    }

    changeFirmDefaultLodgementReceiptEmailAddress(){
        this.surveyFirm.firmDefaultLodgementReceiptEmailAddress = true;
    }

    hasMaintainFirmPermission() {
        return this.permissionsService.getPermissions(this.maintainPermissions).length > 0;
    }
}
